﻿import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import type { ApiError } from '@pms/shared';
import { assetsService, type AssetStatus } from '@/services/assets.service';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';

interface Props {
  assetId: string;
  currentStatusId: string;
  currentStatusName: string;
  currentStatusColor: string;
  statuses: AssetStatus[];
}

function StatusDot({ color }: { color: string }) {
  return (
    <span
      className="inline-block h-2 w-2 rounded-full shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

export function AssetStatusDropdown({
  assetId,
  currentStatusId,
  currentStatusName,
  currentStatusColor,
  statuses,
}: Props) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (statusId: string) => assetsService.updateAssetStatus(assetId, statusId),
    onSuccess: () => {
      toast.success('Status updated');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Select
      value={currentStatusId}
      onValueChange={(v) => mutation.mutate(v)}
      disabled={mutation.isPending}
    >
      <SelectTrigger className="h-8 w-40 text-xs">
        {/* !flex: SelectTrigger's [&>span]:line-clamp-1 would otherwise reset display and drop the gap */}
        <span className="!flex items-center gap-2 min-w-0">
          <StatusDot color={currentStatusColor} />
          <span className="truncate">{currentStatusName}</span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {statuses.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            <span className="flex items-center gap-2">
              <StatusDot color={s.color} />
              {s.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
